import React, { useState, useContext, useEffect } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { CSSTransition } from 'react-transition-group'
import { useQuestionTrack } from 'api/tracking'
import { CREATE_RESPONSE_MUTATION } from 'api/queries'
import { IResponse, singleOrMultiAnswers, IResult } from 'models'
import { StateContext, DispatchContext } from 'store/contexts'
import {
  GO_TO_NEXT_QUESTION,
  FILTER_QUESTIONS,
  ADD_ANSWERS,
  ADD_RESULT,
  GO_TO_HAND_SCREEN,
  FILTER_PRODUCTS,
} from 'store/actions'
import Question from 'components/Questions/Question'
import { IQuestionsFilterPayload } from 'store/reducers'
import { Flex, Button } from 'components'
import { currentQuestionSelector } from 'store/selectors'

interface CreateResult {
  responses: IResponse[]
  referrer: string
}

interface CreateResultReturnValue {
  createResult: Pick<IResult, 'id' | 'hand'>
}

const Wizard = () => {
  const state = useContext(StateContext)
  const dispatch = useContext(DispatchContext)

  const { filteredQuestions, currentQuestionIndex } = state.questions
  const responses = state.responses
  // Current Question or last Question (the last question is displayed during the submit loading
  // phase) which makes it appear like the screen hasn't changed. This allows the display of a
  // loader button, instead of a blank screen / loading screen.
  const currentQuestion = currentQuestionSelector(state)
  // isLastQuestion is not true from a UI perspective. When the last question is being displayed
  // in the UI, the current state is the question index before the last one.
  const isLastQuestion = currentQuestionIndex === filteredQuestions.length
  const isUiLastQuestion = currentQuestionIndex === filteredQuestions.length - 1

  useQuestionTrack(currentQuestion)
  // capture current question answers, we only display one question at a time
  const [answers, setAnswers] = useState<IResponse['answers']>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [inQuestion, setInQuestion] = useState(true) // apply question transition animation

  const [createResult] = useMutation<
    CreateResultReturnValue,
    { result: CreateResult }
  >(CREATE_RESPONSE_MUTATION)

  const onNextStep = () => {
    // check if the question has filter, and store it if it does
    if (
      currentQuestion &&
      currentQuestion.category === 'filter' &&
      !isLastQuestion
    ) {
      dispatch({
        type: FILTER_QUESTIONS,
        payload: {
          filterCategory: singleOrMultiAnswers(answers).map((a) => a.value),
          filterRef: { id: currentQuestion.id },
        } as IQuestionsFilterPayload,
      })
    }

    dispatch({
      type: ADD_ANSWERS,
      payload: {
        question: currentQuestion,
        answers,
      },
    })

    dispatch({ type: GO_TO_NEXT_QUESTION })

    // reset the answers array for the next question
    setAnswers([])

    // scroll to top after answering each question
    if (!isUiLastQuestion) {
      window.scrollTo(0, 0)
    }
  }

  const onSubmit = React.useCallback(() => {
    setIsSubmitting(true)

    const result = {
      // Only send question id in create payload (reduce payload size / complexity)
      responses: responses.map((res) => ({
        ...res,
        question: { id: res.question.id },
      })),
      referrer: document.referrer,
    }

    createResult({ variables: { result: result as CreateResult } })
      .then(({ data }) => {
        if (data) {
          dispatch({ type: ADD_RESULT, payload: data.createResult })
          dispatch({
            type: FILTER_PRODUCTS,
            payload: { hand: data.createResult.hand },
          })
        }
        dispatch({ type: GO_TO_HAND_SCREEN })
      })
      .catch((err) => {
        console.error(err)
        setIsSubmitting(false)
      })
  }, [responses, createResult, dispatch])

  const handleQuestionChange = React.useCallback(
    (newAnswers: IResponse['answers']) => {
      setAnswers(newAnswers)
    },
    [setAnswers]
  )

  useEffect(() => {
    if (isLastQuestion && !isSubmitting) {
      onSubmit() // submit the data even if there is no question answered
    }
  }, [isLastQuestion, onSubmit, isSubmitting])

  if (currentQuestion) {
    return (
      <Flex
        height='100%'
        width={1}
        flexDirection='column'
        className='question-screen'
        padding={[2, null, '16px 10%', '16px 15%', '16px 30%']}
        justifyContent={['space-between', 'space-between', 'center']}
      >
        <Flex height={['100%', '100%', 'auto', 'auto']}>
          {currentQuestion && (
            <CSSTransition
              in={inQuestion}
              timeout={200}
              classNames='oof-question'
              onExit={() => {
                onNextStep()
                setInQuestion(true)
              }}
            >
              <Question
                question={currentQuestion}
                onChange={handleQuestionChange}
              />
            </CSSTransition>
          )}
        </Flex>
        <Flex justifyContent='center'>
          <Button
            full
            maxWidth='290px'
            marginTop={4}
            marginBottom={3}
            uiColorTheme={
              currentQuestion?.theming
                ? currentQuestion.theming.accent
                : 'light'
            }
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={() => setInQuestion(false)}
          >
            {isUiLastQuestion ? 'Finish' : answers.length > 0 ? 'Next' : 'Skip'}
          </Button>
        </Flex>
      </Flex>
    )
  } else {
    return <span>'Loading...'</span>
  }
}

export default Wizard
